/**
 * @file: useLeadListControls.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Single owner of every lead-list control: the status tab, the debounced search
 * keyword, the sort order, the owner (assignee) filter, the origin + intention
 * pickers, and the target-date range. Combines them into the `filter` /
 * `keyword` / `sorter` the list + stats queries consume, and exposes the display
 * state the control bar renders from.
 */

import { LeadListFilter, LeadSorter } from '@euler/model/lead/types';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map } from 'rxjs';
import {
  LeadDateField,
  LeadDatePresetKey,
  kLeadDateFields,
  kLeadIntentionOptions,
  kLeadOriginOptions,
  kLeadSortOptions,
  kLeadStatusTabs,
} from './leadListConstants';

const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
const short = (iso: string) => format(new Date(`${iso}T00:00:00`), 'MM-dd');

/** Resolve a preset (plus any custom bounds) to a yyyy-mm-dd start/end pair. */
const resolveRange = (
  preset: LeadDatePresetKey,
  customStart: Date | undefined,
  customEnd: Date | undefined,
): { start?: string; end?: string } => {
  const now = new Date();
  switch (preset) {
    case 'thisMonth':
      return { start: fmt(startOfMonth(now)), end: fmt(endOfMonth(now)) };
    case 'lastMonth': {
      const prev = subMonths(now, 1);
      return { start: fmt(startOfMonth(prev)), end: fmt(endOfMonth(prev)) };
    }
    case 'last7':
      return { start: fmt(subDays(now, 6)), end: fmt(now) };
    case 'custom': {
      // Normalize an inverted pick (end before start) by swapping, so the range
      // never silently matches zero rows.
      let from = customStart;
      let to = customEnd;
      if (from && to && from > to) [from, to] = [to, from];
      return {
        start: from ? fmt(from) : undefined,
        end: to ? fmt(to) : undefined,
      };
    }
    case 'all':
    default:
      return {};
  }
};

export const useLeadListControls = () => {
  const [statusKey, setStatusKey] = useState('all');
  // The raw search input drives an RxJS subject; the debounced + trimmed value
  // emitted from it becomes the `keyword` that queries consume.
  const searchText$ = useCreation(() => new BehaviorSubject<string>(''), []);
  const [searchText, setSearchText] = useBehaviorSubject(searchText$);
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState(kLeadSortOptions[0].key);
  const [assignee, setAssignee] = useState<
    { id: number; name: string } | undefined
  >(undefined);
  // Both default to the `all` option, which leaves their filter field unset.
  const [originKey, setOriginKey] = useState(kLeadOriginOptions[0].key);
  const [intentionKey, setIntentionKey] = useState(
    kLeadIntentionOptions[0].key,
  );
  const [dateField, setDateField] =
    useState<LeadDateField>('targetServiceDate');
  const [datePreset, setDatePreset] = useState<LeadDatePresetKey>('all');
  const [customStart, setCustomStart] = useState<Date | undefined>(undefined);
  const [customEnd, setCustomEnd] = useState<Date | undefined>(undefined);

  // Debounce the raw input into the value that actually drives the queries, so
  // each keystroke doesn't fire a list + stats request. `distinctUntilChanged`
  // drops no-op edits (e.g. trailing whitespace that trims to the same text).
  useEffect(() => {
    const sub = searchText$
      .pipe(
        debounceTime(300),
        map(text => text.trim()),
        distinctUntilChanged(),
      )
      .subscribe(setKeyword);
    return () => sub.unsubscribe();
  }, [searchText$]);

  const range = useMemo(
    () => resolveRange(datePreset, customStart, customEnd),
    [datePreset, customStart, customEnd],
  );

  const filter = useMemo<LeadListFilter | undefined>(() => {
    const tab = kLeadStatusTabs.find(t => t.key === statusKey);
    const next: LeadListFilter = {};
    if (tab?.status) next.status = tab.status;
    if (assignee) next.assigneeId = assignee.id;
    // `全部` carries no values, so the field stays off the filter entirely.
    const origin = kLeadOriginOptions.find(o => o.key === originKey)?.values;
    if (origin) next.origin = origin;
    const intention = kLeadIntentionOptions.find(o => o.key === intentionKey)
      ?.values;
    if (intention) next.intention = intention;
    // The debounced keyword runs a backend OR-LIKE over plate / name / phone.
    if (keyword) next.keyword = keyword;
    if (range.start || range.end) {
      // The range targets whichever date column the field selector chose.
      next[dateField] = {
        start: range.start,
        end: range.end,
        startInclusive: true,
        endInclusive: true,
      };
    }
    return Object.keys(next).length ? next : undefined;
  }, [
    statusKey,
    assignee,
    originKey,
    intentionKey,
    keyword,
    dateField,
    range.start,
    range.end,
  ]);

  const sorter = useMemo<LeadSorter>(
    () =>
      (kLeadSortOptions.find(o => o.key === sortKey) ?? kLeadSortOptions[0])
        .sorter,
    [sortKey],
  );

  // Picking a fresh start clears any stale end so the previous upper bound can't
  // outlive it; the end is then chosen against the new start. An explicit bound
  // pick always wins over the active preset, so switch to 'custom' here.
  const onCustomStart = useCallback((date: Date) => {
    setCustomStart(date);
    setCustomEnd(undefined);
    setDatePreset('custom');
  }, []);
  const onCustomEnd = useCallback((date: Date) => {
    setCustomEnd(date);
    setDatePreset('custom');
  }, []);

  // Clearing one bound leaves the other in force — the backend turns a
  // half-open range into a plain `>=` / `<=`. Clearing the last one leaves
  // nothing custom about the range, so fall back to 全部 rather than let the
  // preset chip keep claiming 自定义 over an empty range.
  const onClearStart = useCallback(() => {
    setCustomStart(undefined);
    if (!customEnd) setDatePreset('all');
  }, [customEnd]);
  const onClearEnd = useCallback(() => {
    setCustomEnd(undefined);
    if (!customStart) setDatePreset('all');
  }, [customStart]);

  const clearDateRange = useCallback(() => {
    setCustomStart(undefined);
    setCustomEnd(undefined);
    setDatePreset('all');
  }, []);

  // A non-custom preset supplies its own bounds, so any custom pair goes with
  // it — otherwise the old bounds silently reappear the next time 自定义 is
  // chosen.
  const selectDatePreset = useCallback((key: LeadDatePresetKey) => {
    if (key !== 'custom') {
      setCustomStart(undefined);
      setCustomEnd(undefined);
    }
    setDatePreset(key);
  }, []);

  const dateFieldLabel = useMemo(
    () => kLeadDateFields.find(f => f.key === dateField)?.label ?? '目标日期',
    [dateField],
  );

  // The resolved bounds rendered into the start/end fields (MM-DD, or 不限 when
  // that side is open), derived from the same `range` the filter consumes.
  const rangeStart = useMemo(
    () => (range.start ? short(range.start) : '不限'),
    [range.start],
  );
  const rangeEnd = useMemo(
    () => (range.end ? short(range.end) : '不限'),
    [range.end],
  );

  return {
    // Combined query inputs. The keyword is folded into `filter.keyword`.
    filter,
    sorter,
    // Status tab.
    statusKey,
    setStatusKey,
    // Search.
    searchText,
    setSearchText,
    // Sort.
    sortKey,
    setSortKey,
    // Owner.
    assignee,
    setAssignee,
    // Origin + intention.
    originKey,
    setOriginKey,
    intentionKey,
    setIntentionKey,
    // Date field + range.
    dateField,
    setDateField,
    dateFieldLabel,
    datePreset,
    selectDatePreset,
    customStart,
    customEnd,
    onCustomStart,
    onCustomEnd,
    onClearStart,
    onClearEnd,
    clearDateRange,
    rangeStart,
    rangeEnd,
  };
};

export type LeadListControls = ReturnType<typeof useLeadListControls>;
